import {Button, Modal, Stack, Text, Textarea, TextInput} from '@mantine/core';
import {useForm} from '@mantine/form';
import {notifications} from '@mantine/notifications';
import {useLocation} from '@tanstack/react-router';
import {useCreateFeedback} from "@/features/incidentIntegration/api/incidentIntegrationQueries.ts";

interface FeedbackModalProps {
    opened: boolean;
    onClose: () => void;
}

interface FeedbackFormData {
    title: string;
    description: string;
}

export const FeedbackModal = ({opened, onClose}: FeedbackModalProps) => {
    const createFeedbackMutation = useCreateFeedback();
    const location = useLocation();

    const form = useForm<FeedbackFormData>({
        mode: 'controlled',
        initialValues: {
            title: '',
            description: '',
        },
        validate: {
            title: (value) => {
                if (!value || value.trim().length === 0) {
                    return 'Please provide a title';
                }
                if (value.trim().length < 5) {
                    return 'Title must be at least 5 characters';
                }
                return null;
            },
            description: (value) => {
                if (!value || value.trim().length === 0) {
                    return 'Please provide feedback description';
                }
                if (value.trim().length < 10) {
                    return 'Please provide at least 10 characters';
                }
                return null;
            },
        },
    });

    const handleSubmit = async (values: FeedbackFormData) => {
        // Submit feedback without blocking UI
        createFeedbackMutation.mutate({
            webAppHref: location.href,
            title: values.title.trim(),
            description: values.description.trim(),
        });

        // Show notification that feedback is being submitted
        notifications.show({
            title: 'Submitting feedback',
            message: 'Your feedback is being sent to the CadCare Connect Team. We\'ll notify you when it\'s been received.',
            color: 'blue',
        });

        // Close modal immediately to not block UI
        form.reset();
        onClose();
    };

    const handleClose = () => {
        form.reset();
        onClose();
    };

    return (
            <Modal
                    opened={opened}
                    onClose={handleClose}
                    title="Submit Feedback"
                    size="lg"
                    aria-labelledby="feedback-modal-title"
            >
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            Your feedback will be automatically associated with your user account and current customer
                            context.
                            This helps our team provide better support and track feature requests.
                        </Text>

                        <TextInput
                                label="Title"
                                placeholder="Brief summary of your feedback..."
                                {...form.getInputProps('title')}
                                disabled={createFeedbackMutation.isPending}
                                required
                                maxLength={255} // Max string length for devops work item title. Keep the same as what's set in CreateFeedbackWorkItemValidator.
                                description={`${form.values.title.length}/255 characters`}
                                aria-label="feedback-title"
                        />

                        <Textarea
                                label="Description"
                                placeholder="Please provide detailed information about your feedback, bug report, or feature request..."
                                rows={6}
                                {...form.getInputProps('description')}
                                disabled={createFeedbackMutation.isPending}
                                required
                                maxLength={3500} // Keep the same as what's set in CreateFeedbackWorkItemValidator. Technically a devops description can be 4000 characters but I read some comments saying they have issues that high and 3500 is safer.
                                description={`${form.values.description.length}/3500 characters`}
                                aria-label="feedback-description"
                        />

                        <Button
                                type="submit"
                                loading={createFeedbackMutation.isPending}
                                disabled={!form.values.title?.trim() || !form.values.description?.trim()}
                                aria-label="submit-feedback-button"
                        >
                            Submit Feedback
                        </Button>
                    </Stack>
                </form>
            </Modal>
    );
};